import { Box, Button, Input, TextField, makeStyles } from '@material-ui/core';
import React, { FC, useState } from 'react';
import { ChromePicker } from 'react-color';
import { useDispatch, useSelector } from 'react-redux';
import useComponentVisible from 'src/hooks/useComponentVisible';
import colorRectangle from 'src/images/colorRectangle.png';
import { RootState } from 'src/store';
import {
  editBtnText,
  editBtnFontSize,
  editBtnFontWeight,
  editBtnFontFamily,
  editBtnColor
} from 'src/store/modules/buttons';

import Plus from 'src/components/Icon/Plus';
import Minus from 'src/components/Icon/Minus';

import ChevronUp from 'src/components/Icon/ChevronUp';
import ChevronDown from 'src/components/Icon/ChevronDown';
import FontFamilyPicker from '../FontFamilyPicker';
import EditBtnBgColor from '../EditBtnBgColorRegurcy';

interface editBtnProps {
  menuTitle: string;
  type: string;
}

const FONT_WEIGHT_ENUM: any = {
  300: 'Light',
  400: 'Regular',
  500: 'Medium',
  600: 'SemiBold',
  700: 'Bold',
  '': 'Custom'
};

const useStyles = makeStyles(() => ({
  root: {
    padding: '1.5rem 1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem'
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
    border: '1px solid #dddddd'
  },
  fontBox: {
    width: '100%',
    height: '2.5rem',
    padding: '0 0.5rem',

    cursor: 'pointer',
    fontFamily: 'Roboto',
    fontSize: '0.75rem',
    fontWeight: 400,

    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    border: '1px solid #D9D9D9'
  },
  input: {
    height: '4.5rem',
    padding: '0.5rem',
    outline: 'none',
    overflow: 'hidden',

    '&:focus': {
      border: 0,
      outline: 'none'
    },

    border: '1px solid #dddddd'
  },
  inputRoot: {
    padding: 0,
    height: '100%',
    display: 'block'
  },
  inputText: {
    fontFamily: 'Roboto',
    fontSize: '0.75rem',
    fontWeight: 400,
    lineHeight: '1.2rem',
    letterSpacing: '0.015rem'
  },

  buttonWrapper: {
    maxWidth: '1.5rem',
    minWidth: '1.5rem',
    maxHeight: '1.5rem',
    minHeight: '1.5rem'
  },
  buttonImage: {
    maxWidth: '1.5rem',
    minWidth: '1.5rem',
    maxHeight: '1.5rem',
    minHeight: '1.5rem'
  },

  bottomBtn: {
    padding: '0.5rem',
    display: 'flex',
    justifyContent: 'space-between',
    border: '1px solid #dddddd',
    backgroundColor: '#F2F2F2'
  },
  btnMinusPlus: {
    maxWidth: '1.5rem',
    minWidth: '1.5rem',
    maxHeight: '1.5rem',
    minHeight: '1.5rem',
    padding: 0,
    display: 'flex'
  },

  fontNumber: {
    width: '3rem',
    height: '1.5rem',

    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',

    fontFamily: 'Roboto',
    fontSize: '0.75rem',
    fontWeight: 400,

    borderRadius: '0.25rem',
    border: '1px solid #D9D9D9',
    backgroundColor: '#fff'
  },

  boxWrapper: {
    // margin: '0 0.5rem',
    display: 'flex',
    justifyContent: 'space-between'
  },
  colorBox: {
    padding: '0.25rem 0.5rem',
    borderRadius: '0.25rem',
    border: '1px solid #D9D9D9',
    textAlign: 'center',

    color: '#000',
    fontFamily: 'Roboto',
    fontSize: '0.75rem',
    fontWeight: 400
  },
  textAlign: {
    cursor: 'pointer'
  }
}));

const EditBtn: FC<editBtnProps> = ({ menuTitle, type }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  let btnText = useSelector((state: RootState) => state.buttons.btnText);
  let btnPreFontSize = useSelector(
    (state: RootState) => state.buttons.btnPreFontSize
  );
  let btnFontSize = useSelector(
    (state: RootState) => state.buttons.btnFontSize
  );
  let btnFontWeight = useSelector(
    (state: RootState) => state.buttons.btnFontWeight
  );
  let btnColor = useSelector((state: RootState) => state.buttons.btnColor);
  let btnFontFamily = useSelector(
    (state: RootState) => state.buttons.btnFontFamily
  );

  const {
    refPicker,
    refButton1,
    refButton2,
    isComponentVisible,
    setIsComponentVisible
  } = useComponentVisible(false);
  const [isFontFamily, setIsFontFamily] = useState(false);

  const handleBtnColorPicker = () => {
    setIsComponentVisible(!isComponentVisible);
  };

  const [key, setKey] = useState(false);
  const handleKey = e => {
    if (e.key === 'Enter') setKey(true);
    else setKey(false);
  };
  const handleBtnText = e => {
    const textSplit = e.target.value.split('');
    if (
      textSplit[textSplit.length - 1] === '\\' ||
      textSplit[textSplit.length - 1] === '"' ||
      textSplit[textSplit.length - 1] === '\n'
    )
      return;

    const newBtnText = JSON.parse(`{"${type}": "${e.target.value}"}`);

    dispatch(editBtnText({ ...btnText, ...newBtnText }));
  };

  const handleBtnFontSize = f => {
    const newBtnFontSize = JSON.parse(`{"${type}": ${btnFontSize[type] + f}}`);
    dispatch(editBtnFontSize({ ...btnFontSize, ...newBtnFontSize }));
  };

  const handleBtnFontFamily = (family: string, weight: number | string) => {
    const newBtnFontFamily = JSON.parse(`{"${type}": "${family}"}`);
    const newBtnFontWeight = JSON.parse(`{"${type}": "${weight}"}`);
    dispatch(editBtnFontFamily({ ...btnFontFamily, ...newBtnFontFamily }));
    dispatch(editBtnFontWeight({ ...btnFontWeight, ...newBtnFontWeight }));
    setIsFontFamily(false);
  };

  const handleBtnColor = c => {
    const newBtnColor = JSON.parse(`{"${type}": "${c.hex}"}`);
    dispatch(editBtnColor({ ...btnColor, ...newBtnColor }));
  };

  return (
    <div className={classes.root}>
      <div
        style={{
          fontSize: '0.875rem',
          fontWeight: 600,
          fontFamily: 'Archivo',
          letterSpacing: '0.0175rem'
        }}
      >
        {menuTitle}
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem'
        }}
      >
        <div
          className={classes.fontBox}
          onClick={() => {
            setIsFontFamily(!isFontFamily);
          }}
        >
          <span>{`${btnFontFamily[type]} [${
            FONT_WEIGHT_ENUM[btnFontWeight[type]]
          }]`}</span>
          {isFontFamily ? (
            <ChevronUp
              style={{
                width: '1.5rem',
                height: '1.5rem'
              }}
            />
          ) : (
            <ChevronDown
              style={{
                width: '1.5rem',
                height: '1.5rem'
              }}
            />
          )}
        </div>
        {isFontFamily && (
          <FontFamilyPicker
            oriFontFamily={btnFontFamily[type]}
            oriFontWeight={btnFontWeight[type]}
            handleFontFamily={handleBtnFontFamily}
          />
        )}
        {/* <div className={classes.container}> */}
        <>
          <TextField
            className={classes.input}
            id="standard-multiline-flexible"
            multiline
            InputProps={{
              classes: {
                root: classes.inputRoot,
                input: classes.inputText
              },
              disableUnderline: true
            }}
            value={btnText[type]}
            onChange={handleBtnText}
            onKeyPress={handleKey}
          />
          <div className={classes.bottomBtn}>
            <Box style={{ display: 'flex', gap: '1rem' }}>
              <Button
                className={classes.btnMinusPlus}
                disabled={btnFontSize[type] === 5 / 8}
                onClick={() => handleBtnFontSize(-1 / 16)}
              >
                <Minus
                  stroke={btnFontSize[type] === 5 / 8 ? '#BDBDBD' : '#000'}
                />
              </Button>
              <Button
                className={classes.btnMinusPlus}
                disabled={btnFontSize[type] === 4}
                onClick={() => handleBtnFontSize(1 / 16)}
              >
                <Plus stroke={btnFontSize[type] === 4 ? '#BDBDBD' : '#000'} />
              </Button>
            </Box>

            <div className={classes.fontNumber}>{btnFontSize[type] * 16}</div>
          </div>
        </>
        {/* </div> */}
      </div>
      <div>
        <Box className={classes.boxWrapper}>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button
              ref={refButton1}
              onClick={handleBtnColorPicker}
              className={classes.buttonWrapper}
            >
              <img
                alt="color"
                src={colorRectangle}
                className={classes.buttonImage}
              />
            </Button>
            <Button
              ref={refButton2}
              style={{
                maxWidth: '1.25rem',
                minWidth: '1.25rem',
                maxHeight: '1.25rem',
                minHeight: '1.25rem',
                padding: 0
              }}
              onClick={handleBtnColorPicker}
            >
              <div
                style={{
                  width: '1.25rem',
                  height: '1.25rem',
                  backgroundColor: btnColor[type],
                  border: `1px solid #D9D9D9`,
                  borderRadius: 999
                }}
              />
            </Button>
            <div className={classes.colorBox}>
              {btnColor[type]?.toUpperCase()}
            </div>
          </div>
          {/* <div className={classes.colorBox}>100%</div> */}
        </Box>
        {isComponentVisible && (
          <div ref={refPicker} style={{ width: '100%', marginTop: '1rem' }}>
            <ChromePicker
              color={btnColor[type]}
              onChange={handleBtnColor}
              disableAlpha={true}
              width={'100%'}
            />
          </div>
        )}
      </div>
      <div
        style={{
          fontSize: '0.875rem',
          fontWeight: 600,
          fontFamily: 'Archivo',
          letterSpacing: '0.0175rem'
        }}
      >
        BUTTON COLOR
      </div>
      <EditBtnBgColor type={type} />
    </div>
  );
};

export default EditBtn;
